import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ButtonControl, FieldControl, Loading } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  buildWizardValidationError,
  getDisplayName,
  getId,
  getLinkedRecord,
  getLinkedRecordItemId,
  getOperationItems,
  getProductResourceDefaults,
  readValue,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const LINKED_RESOURCE_FORM_GROUP_ID = "resourceWizardLinkedResourceConfiguration";
const CONFIGURATION_STATUS_RETIRED = 6;
const CONFIGURATION_STATUS_LABELS = {
  1: "Draft",
  2: "Configuring",
  3: "Pending Review",
  4: "Active",
  5: "Failed",
  6: "Retired",
  7: "Configured"
};
const ENTITY_SCHEMA_PRODUCT_KEY = "entityschema";
const ENTITY_SCHEMA_MARKETPLACE_PATH = "/marketplace/options/entity-schema";
const LOADING_INDICATOR_DELAY_MS = 150;

const fieldValue = (nextValue) => {
  if (nextValue && typeof nextValue === "object") {
    return nextValue.Value ?? nextValue.value ?? nextValue._id ?? nextValue.id ?? "";
  }
  return nextValue ?? "";
};

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const normalizeResourceKey = (value) => normalizeText(value).replace(/[^a-z0-9]/g, "");

const resourceDisplayLabel = (value) =>
  String(value || "Resource")
    .replace(/\s+configuration$/i, "")
    .trim() || "Resource";

const getLinkedConfigurationId = (wizard) =>
  getLinkedRecordItemId(getLinkedRecord(wizard, "ResourceConfiguration", "EntitySchemaResourceConfiguration")) || "";

const getSchemaLinkId = (configuration) =>
  getLinkedRecordItemId(
    (readValue(configuration, "LinkedRecords", "linkedRecords") || []).find((record) =>
      normalizeText(readValue(record, "ItemName", "itemName")) === "schema"
      && normalizeText(readValue(record, "Relationship", "relationship")) === "entityschema"
    )
  );

const configurationStatusValue = (configuration) =>
  Number(readValue(configuration, "ConfigurationStatus", "configurationStatus", "Status", "status"));

const configurationStatusLabel = (configuration) => {
  const displayName = readValue(configuration, "ConfigurationStatusDisplayName", "configurationStatusDisplayName");
  if (displayName) return displayName;

  const statusValue = configurationStatusValue(configuration);
  if (CONFIGURATION_STATUS_LABELS[statusValue]) return CONFIGURATION_STATUS_LABELS[statusValue];

  return readValue(configuration, "ConfigurationStatus", "configurationStatus", "Status", "status") || "";
};

const getResourceConfigurationKeys = (configuration) => [
  readValue(configuration, "ProductKey", "productKey"),
  readValue(configuration, "ProductResourceKey", "productResourceKey"),
  readValue(configuration, "ResourceProductKey", "resourceProductKey"),
  readValue(configuration, "ProductResourceDefinitionId", "productResourceDefinitionId")
].map(normalizeResourceKey).filter(Boolean);

const configurationMatchesLinkedResource = (configuration, linkedResourceKeys) =>
  getResourceConfigurationKeys(configuration).some((key) => linkedResourceKeys.has(key));

const configurationOption = (configuration) => {
  const id = getId(configuration);
  const status = configurationStatusLabel(configuration);
  const schemaId = getSchemaLinkId(configuration);
  const suffix = [status, schemaId ? "Schema linked" : "Schema pending"].filter(Boolean).join(" - ");
  return {
    Text: `${getDisplayName(configuration) || id}${suffix ? ` (${suffix})` : ""}`,
    Value: id,
    configuration
  };
};

const buildQuery = () => ({
  model: "x => x.IsDeleted == false"
});

const LinkedResourceConfigurationSelectorStep = ({
  context,
  onChange,
  onSave,
  onContinue,
  registerStepController,
  resourceConfigurationManager
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreState = resourceDomainContext?.coreState;
  const resourceConfigurationMgr = resourceConfigurationManager || resourceDomainContext?.managers?.resourceConfiguration;
  const productResourceDefaults = context.productResourceDefaults || getProductResourceDefaults(wizard);
  const linkedProductKey = readValue(values, "LinkedResourceProductKey", "linkedResourceProductKey")
    || readValue(productResourceDefaults, "linkedResourceProductKey", "LinkedResourceProductKey")
    || "EntitySchema";
  const linkedProductResourceKey = readValue(values, "LinkedResourceProductResourceKey", "linkedResourceProductResourceKey")
    || readValue(productResourceDefaults, "linkedResourceProductResourceKey", "LinkedResourceProductResourceKey")
    || linkedProductKey;
  const linkedLabel = readValue(productResourceDefaults, "linkedResourceLabel", "LinkedResourceLabel")
    || "Entity Schema Resource Configuration";
  const displayLabel = resourceDisplayLabel(linkedLabel);
  const displayLabelArticle = /^[aeiou]/i.test(String(displayLabel || "").trim()) ? "an" : "a";
  const hasResourceConfigurationQuery = Boolean(resourceConfigurationMgr?.Query);
  const currentResourceConfigurationId = readValue(wizard, "ResourceConfigurationId", "resourceConfigurationId")
    || getId(readValue(wizard, "ResourceConfiguration", "resourceConfiguration"));
  const persistedLinkedId = getLinkedConfigurationId(wizard);
  const initialLinkedId = readValue(values, "LinkedResourceConfigurationId", "linkedResourceConfigurationId")
    || persistedLinkedId
    || "";
  const [selectedId, setSelectedId] = useState(initialLinkedId);
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const selectedIdRef = useRef(initialLinkedId);
  const onChangeRef = useRef(onChange);
  const loadRequestIdRef = useRef(0);
  const coreStateRef = useRef(coreState);
  const displayLabelRef = useRef(displayLabel);
  const resourceConfigurationMgrRef = useRef(resourceConfigurationMgr);
  const linkedResourceKeys = useMemo(() => new Set(
    [linkedProductKey, linkedProductResourceKey].map(normalizeResourceKey).filter(Boolean)
  ), [linkedProductKey, linkedProductResourceKey]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    coreStateRef.current = coreState;
    displayLabelRef.current = displayLabel;
    resourceConfigurationMgrRef.current = resourceConfigurationMgr;
  }, [coreState, displayLabel, resourceConfigurationMgr]);

  useEffect(() => {
    if (!loading) {
      setShowLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowLoading(true);
    }, LOADING_INDICATOR_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loading]);

  useEffect(() => {
    let mounted = true;
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    const isCurrentRequest = () => mounted && loadRequestIdRef.current === requestId;
    const manager = resourceConfigurationMgrRef.current;

    if (!manager?.Query) {
      setLoading(false);
      setLocalError("Resource Configuration service is not available.");
      return undefined;
    }

    setLoading(true);
    setLocalError("");
    manager.Query(buildQuery())
      .then((result) => {
        if (!isCurrentRequest()) return;
        requireSuccessfulServiceResponse(result, `${displayLabelRef.current} records could not be loaded.`, coreStateRef.current);
        const rows = getOperationItems(result)
          .filter((configuration) => configurationMatchesLinkedResource(configuration, linkedResourceKeys))
          .filter((configuration) => getId(configuration) !== currentResourceConfigurationId)
          .filter((configuration) => configurationStatusValue(configuration) !== CONFIGURATION_STATUS_RETIRED)
          .sort((left, right) => String(getDisplayName(left)).localeCompare(String(getDisplayName(right))));
        setConfigurations(rows);
        if (selectedIdRef.current && !rows.some((row) => getId(row) === selectedIdRef.current)) {
          setSelectedId("");
          onChangeRef.current?.("LinkedResourceConfigurationId", "");
        }
      })
      .catch((error) => {
        if (!isCurrentRequest()) return;
        setLocalError(error?.message || `${displayLabelRef.current} records could not be loaded.`);
        setConfigurations([]);
      })
      .finally(() => {
        if (isCurrentRequest()) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [currentResourceConfigurationId, hasResourceConfigurationQuery, linkedProductKey, linkedResourceKeys]);

  const options = useMemo(() => configurations.map(configurationOption), [configurations]);
  const selectedConfiguration = useMemo(
    () => configurations.find((configuration) => getId(configuration) === selectedId) || null,
    [configurations, selectedId]
  );
  const hasConfigurations = configurations.length > 0;
  const showMarketplaceLink = normalizeText(linkedProductKey) === ENTITY_SCHEMA_PRODUCT_KEY;

  const updateSelection = useCallback((nextValue) => {
    const nextId = fieldValue(nextValue);
    setSelectedId(nextId);
    onChange?.("LinkedResourceConfigurationId", nextId);
  }, [onChange]);

  const prepareValues = useCallback((currentValues = {}) => {
    const linkedId = selectedIdRef.current;
    if (!linkedId) throw buildWizardValidationError(`Select ${displayLabelArticle} ${displayLabel}.`);
    return {
      ...(currentValues || {}),
      LinkedResourceConfigurationId: linkedId,
      LinkedResourceProductKey: linkedProductKey,
      LinkedResourceProductResourceKey: linkedProductResourceKey,
      LinkedSchemaId: selectedConfiguration ? getSchemaLinkId(selectedConfiguration) : ""
    };
  }, [displayLabel, displayLabelArticle, linkedProductKey, linkedProductResourceKey, selectedConfiguration]);

  useEffect(() => {
    registerStepController?.({
      skipStepInputValidation: true,
      prepareSave: prepareValues,
      prepareContinue: prepareValues
    });
    return () => registerStepController?.(null);
  }, [prepareValues, registerStepController]);

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title={displayLabel}
        description="Select the reusable resource this setup will map into and depend on. After setup, management can refine mappings and use the selected resource as the shared model for downstream destinations."
        countText={selectedId ? "1 selected" : "Required"}
      />
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      {localError ? null : loading ? (
        showLoading ? (
          <div className="resource-wizard__linked-step-loading"><Loading /></div>
        ) : (
          <div className="resource-wizard__linked-step-loading" aria-hidden="true" />
        )
      ) : !hasConfigurations ? (
        <div className="resource-wizard__empty">
          <strong>No {displayLabel}s are available.</strong>
          <p>Setup needs {displayLabelArticle} {displayLabel} before this resource can continue. Create or configure one, then return to this step and select it.</p>
          {showMarketplaceLink ? (
            <Link className="resource-wizard__button resource-wizard__button--secondary" to={ENTITY_SCHEMA_MARKETPLACE_PATH}>
              Create Entity Schema Resource
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="resource-wizard__fields">
          <div className="resource-wizard__field">
            <FieldControl
              className="resource-wizard__field-control"
              fieldMetadata={{
                FieldName: "LinkedResourceConfigurationId",
                DisplayName: displayLabel,
                FieldType: SystemTypes.UIFieldType.SearchSelectList,
                IsRequired: true,
                DataJson: JSON.stringify(options)
              }}
              selectData={options}
              value={selectedId}
              disabled={readOnly || saving}
              groupId={LINKED_RESOURCE_FORM_GROUP_ID}
              onChange={updateSelection}
              disableError
            />
          </div>
        </div>
      )}
      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl type="standard-small" className="resource-wizard__button resource-wizard__button--secondary" onClick={() => onSave?.()} disabled={readOnly || saving || loading} loading={saving}>Save</ButtonControl>
          <ButtonControl type="ok-small" className="resource-wizard__button resource-wizard__button--primary" onClick={() => onContinue?.()} disabled={readOnly || saving || loading || !selectedId} loading={saving}>Save & Continue</ButtonControl>
        </div>
      </div>
    </div>
  );
};

export default LinkedResourceConfigurationSelectorStep;
